import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { WindowDetectService } from '@service/window-detect.service';
import { CartBottomSheetService } from '@service/cart-bottom-sheet.service';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { Price } from '@common/pipes';
import { StepperComponent } from '@common/stepper/stepper.component';
import { Product, Product_SkuColor } from '@proto/store/api/store/product';
import { CartDialogModel } from './cart-dialog-model';
import { CartModel } from '../cart/cart.model';
import { ColorSelectorComponent } from './color-selector/color-selector.component';
import { Toast } from '@helper/toast';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { SizeInfo } from '@helper/size-info';

interface IDialogData {
  skuId: string;
  amount: number;
  destroyOnClose: boolean;
  product?: Product;
  showDetail?: boolean; // open by anchor or non product page
  replace?: boolean;
}

@Component({
  selector: 'app-cart-dialog',
  templateUrl: './cart-dialog.component.html',
  styleUrls: ['./cart-dialog.component.scss'],
  imports: [
    ImageLoadingComponent,
    Price,
    MatDialogModule,
    MatInputModule,
    FormsModule,
    StepperComponent,
    MatProgressSpinnerModule,
    ColorSelectorComponent,
    MatSnackBarModule,
    FocusRemoverDirective,
    NgIf,
    NgForOf,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartDialogComponent {
  readonly baseMarginBottomHeight = 200;
  readonly minHeight = 300;
  readonly data: IDialogData = inject(MAT_BOTTOM_SHEET_DATA);
  // 因為 ios 打開 cart-dialog 可能 window innerHeight 高度都不一樣, 所以以每次打開時的高度去計算.
  readonly maxHeight = window.innerHeight * 0.8 - 62 - 15; // 62px: confirm button height.
  readonly buttonText = computed(() =>
    this.viewModel.currSize()?.stockRemind ? $localize`貨到通知我` : $localize`確定`
  );
  readonly contentHeight = signal(this.minHeight);

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('contentWrapper', { read: ElementRef }) wrapper!: ElementRef;

  constructor(
    public cartViewModel: CartModel,
    public viewModel: CartDialogModel,
    public windowDetectService: WindowDetectService,
    public router: Router,
    private toast: MatSnackBar,
    private cartBottomSheetService: CartBottomSheetService
  ) {
    effect(() => {
      const color = this.viewModel.currColor();
      if (color && this.viewModel.cartSizes().length == 1) {
        this.viewModel.setSize(this.viewModel.cartSizes()[0]);
      }
    });
    effect(() => {
      const colors = this.viewModel.apiResult().colors;
      if (colors.length === 1) {
        this.viewModel.setColor(colors[0]);
      }
    });
    this.cartViewModel.cartAdded.pipe(takeUntilDestroyed()).subscribe(() => {
      Toast.open(this.toast, $localize`加入購物車成功`);
      this.onClose();
    });
    this.cartViewModel.cartReplaced.pipe(takeUntilDestroyed()).subscribe(res => {
      if (res) {
        Toast.open(this.toast, $localize`修改購物車成功`);
      }
      this.onClose();
    });
    this.viewModel.getCartDialogData(
      this.destroyRef,
      this.data.skuId,
      this.data.amount,
      this.data.destroyOnClose,
      this.data.product,
      this.data.showDetail,
      this.data.replace
    );
  }

  static open(
    bottomSheet: MatBottomSheet,
    skuId: string,
    amount: number,
    destroyOnClose: boolean,
    isTablet: boolean,
    product?: Product,
    showDetail?: boolean,
    replace?: boolean
  ): MatBottomSheetRef<CartDialogComponent> {
    const data: IDialogData = { skuId, amount, product, showDetail, destroyOnClose, replace };
    return bottomSheet.open(CartDialogComponent, {
      panelClass: isTablet ? 'tablet-bottom-sheet' : 'bottom-sheet',
      data,
    });
  }

  toSales(): void {
    this.cartBottomSheetService.routeTo(false);
  }

  toDetail(): void {
    this.cartBottomSheetService.routeTo(true);
  }

  openGallery(color: Product_SkuColor): void {
    this.cartBottomSheetService.openGallery(color);
  }

  openFitting(): void {
    this.cartBottomSheetService.openFitting();
  }

  onClose(): void {
    this.cartBottomSheetService.onClose();
  }

  sizeClick(size: SizeInfo | undefined): void {
    if (!size) {
      return;
    }
    if (!this.viewModel.currColor()) {
      Toast.open(this.toast, $localize`請選擇顏色`);
      return;
    }
    this.viewModel.setSize(size);
  }

  stepperMask(): boolean {
    if (!this.viewModel.currColor()) {
      Toast.open(this.toast, $localize`請選擇顏色`);
      return true;
    }
    if (!this.viewModel.currSize()) {
      Toast.open(this.toast, $localize`請選擇尺寸`);
      return true;
    }
    return false;
  }

  addToCart(): void {
    if (this.stepperMask()) {
      return;
    }
    if (this.data.replace) {
      this.viewModel.cartReplace();
      return;
    }
    this.viewModel.addToCart(this.toast);
  }

  trackByFn(_: number, color: Product_SkuColor | undefined): string {
    return color?.id ?? '';
  }

  sizeTrackByFn(_: number, size: SizeInfo | undefined): string {
    return size?.size?.id ?? '';
  }

  updateContentHeight(maxColorHeight: number): void {
    this.contentHeight.set(
      Math.min(this.maxHeight, maxColorHeight + this.wrapper.nativeElement.offsetHeight + this.baseMarginBottomHeight)
    );
  }
}
